import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { api, type TreeNode } from "../../api.js";

/** Rows as the menu and pricing controllers return them (apps/server/src/modules/{menu,pricing}). */
export interface ListRow {
  id: string;
  version: number;
  name: string;
  sortOrder: number;
  active: boolean;
}
export interface ItemRow {
  id: string;
  version: number;
  code: string;
  name: string;
  kind: "food" | "companion";
  description: string;
  active: boolean;
  prepStation: "bread" | "companions" | null;
}
export interface RecipeLine {
  componentId: string;
  perPortionMilli: number;
}
export interface RecipeRow {
  menuItemId: string;
  version: number | null;
  lines: RecipeLine[];
}
export interface EntryRow {
  id: string;
  version: number;
  businessDate: string;
  mealId: string;
  menuItemId: string;
  organizationId: string | null;
  tierIds: string[];
  sortOrder: number;
}
export interface PackageLine {
  companionItemId: string;
  perPortionMilli: number;
  included: boolean;
}
export interface PackageRow {
  id: string;
  version: number;
  lines: PackageLine[];
}
export interface RuleRow {
  id: string;
  version: number;
  level: "unit-item" | "organization-item" | "organization-default" | "item-base";
  unitId: string | null;
  organizationId: string | null;
  menuItemId: string | null;
  tierId: string | null;
  amount: number;
  validFrom: string;
  validTo: string | null;
  note: string;
}

export const useMeals = () =>
  useQuery({ queryKey: ["menu-lists", "meals"], queryFn: () => api<ListRow[]>("/menu/lists/meals") });
export const useTiers = () =>
  useQuery({ queryKey: ["menu-lists", "tiers"], queryFn: () => api<ListRow[]>("/menu/lists/tiers") });
export const useComponents = () =>
  useQuery({
    queryKey: ["menu-lists", "components"],
    queryFn: () => api<ListRow[]>("/menu/lists/components"),
  });
export const useItems = () =>
  useQuery({ queryKey: ["menu-items"], queryFn: () => api<ItemRow[]>("/menu/items") });

/** Organizations (flattened, indented by depth) and units, from the organization tree. */
export function useOrgs() {
  const tree = useQuery({ queryKey: ["organizations"], queryFn: () => api<TreeNode[]>("/organizations") });
  return useMemo(() => {
    const orgs: { id: string; name: string; depth: number }[] = [];
    const units: { id: string; name: string; organizationId: string }[] = [];
    const walk = (nodes: TreeNode[], depth: number) => {
      for (const n of nodes) {
        orgs.push({ id: n.id, name: n.name, depth });
        for (const u of n.units)
          units.push({
            id: u.id,
            name: u.name === n.name ? n.name : `${n.name} — ${u.name}`,
            organizationId: n.id,
          });
        walk(n.children, depth + 1);
      }
    };
    walk(tree.data ?? [], 0);
    return { orgs, units };
  }, [tree.data]);
}

/** Saturday of the week that contains `iso` (the Persian week starts on Saturday). */
export function weekStart(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  const back = (d.getUTCDay() + 1) % 7; // Sat → 0, Sun → 1, …, Fri → 6
  d.setUTCDate(d.getUTCDate() - back);
  return d.toISOString().slice(0, 10);
}

/** "1.5" / "۱٫۵" → 1500 thousandths; null when not a positive amount with at most 3 decimals. */
export function parseMilli(input: string): number | null {
  const ascii = input
    .replace(/[۰-۹]/g, (d) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(d)))
    .replace(/[٠-٩]/g, (d) => String("٠١٢٣٤٥٦٧٨٩".indexOf(d)))
    .replace(/[٫,]/g, ".")
    .trim();
  const m = /^(\d{1,5})(?:\.(\d{1,3}))?$/.exec(ascii);
  if (!m) return null;
  const n = Number(m[1]) * 1000 + Number((m[2] ?? "").padEnd(3, "0") || "0");
  return n > 0 ? n : null;
}

export const formatMilli = (n: number) => {
  const whole = Math.trunc(n / 1000);
  const frac = String(n % 1000)
    .padStart(3, "0")
    .replace(/0+$/, "");
  return frac ? `${String(whole)}٫${frac}` : String(whole);
};
